import { CSSProperties, useEffect, useState } from 'react';

import { DropzoneDialog } from 'material-ui-dropzone';

import { WebPhotoFields } from '../../graphql/athena/types/WebPhotoFields';
import {
  EmptyIconSVG,
  LeftChevronButtonUrl,
  PlusIconUrl,
  RightChevronButtonUrl,
  TrashCanCircleIcon,
} from '../../icons/Icons';
import HorizontalLayout from '../layouts/HorizontalLayout';
import VerticalLayout from '../layouts/VerticalLayout';

export interface File {
  id: string;
  name: string;
  url: string;
}

interface InlinePhotoViewerProps {
  photos: WebPhotoFields[];
  onUpload: (files: any[], kind: string) => void;
  onRemove: (fileName: string) => void;

  // optional title that appears above the InlinePhotoViewer
  title?: string;

  // optional max number of thumbnails to show
  visibleThumbnailsCount?: number;

  allowEdit?: boolean;

  // optional styles
  thumbnailHeight?: string;
  thumbnailWidth?: string;
  thumbnailContainerStyle?: CSSProperties;
  emptyIconStyle?: CSSProperties;
  titleStyle?: CSSProperties;
}

const InlinePhotoViewer = ({
  photos,
  title = '',
  visibleThumbnailsCount = 5,
  allowEdit = false,
  onRemove,
  thumbnailHeight = '55px',
  thumbnailWidth = '55px',
  thumbnailContainerStyle = {},
  emptyIconStyle = { height: '50vh', width: '24vw' },
  titleStyle = { fontWeight: 600, fontSize: 16, color: '#222222' },
  onUpload,
}: InlinePhotoViewerProps) => {
  const [photoIndex, setPhotoIndex] = useState(0);
  const [mainPhoto, setMainPhoto] = useState<WebPhotoFields | null>(
    photos.length - 1 >= photoIndex ? photos[photoIndex] : null,
  );
  const [currentThumbnailCount, setCurrentThumbnailCount] = useState(visibleThumbnailsCount);
  const [showUploader, setShowUploader] = useState(false);

  const backClick = () => {
    if (photoIndex > 0) {
      setPhotoIndex((i) => i - 1);
    }
  };
  const forwardClick = () => {
    const lastPhotoIndex = photos.length - 1;
    if (photoIndex < lastPhotoIndex) {
      setPhotoIndex((i) => i + 1);
    }
  };
  useEffect(() => {
    setMainPhoto(photos[photoIndex]);
  }, [photoIndex, photos]);

  const getMainPhotoComponent = () => {
    if (!!mainPhoto) {
      return (
        <div style={{ ...styles.fullImageContainer, position: 'relative' }}>
          <img
            src={LeftChevronButtonUrl}
            alt="left-chevron-button-url"
            style={{ cursor: 'pointer', position: 'absolute', left: '5%' }}
            onClick={backClick}
            data-testid="left-chevron-button"
          />
          <img
            src={RightChevronButtonUrl}
            alt="right-chevron-button-url"
            style={{ cursor: 'pointer', position: 'absolute', right: '5%' }}
            onClick={forwardClick}
            data-testid="right-chevron-button"
          />
          {allowEdit ? (
            <img
              src={TrashCanCircleIcon}
              alt="trash-can-circle-icon"
              style={{ cursor: 'pointer', position: 'absolute', top: '5%', right: '5%' }}
              onClick={() => onRemove(mainPhoto.originalFilename)}
              data-testid="trash-can-circle-icon"
            />
          ) : null}
          <img
            src={mainPhoto.fullSizeUrl || ''}
            alt=""
            style={{ ...styles.fullImage, objectFit: 'contain' }}
            data-testid="main-photo"
          />
        </div>
      );
    }
    return <EmptyIconSVG emptyIconStyle={emptyIconStyle} />;
  };
  const openUploaderButton = (
    <VerticalLayout style={thumbnailContainerStyle}>
      <div
        data-testid="open-uploader-button"
        style={{
          ...styles.addImage,
          borderStyle: 'solid',
          height: thumbnailHeight,
          width: thumbnailWidth,
        }}
        onClick={() => {
          setShowUploader(true);
        }}
      >
        <img src={PlusIconUrl} alt="" />
      </div>
    </VerticalLayout>
  );
  const extraImgCount = (
    <div
      data-testid="extra-img-ct"
      onClick={() => {
        setCurrentThumbnailCount(photos.length);
      }}
      style={{
        ...styles.extraImgCount,
        height: thumbnailHeight,
        width: thumbnailWidth,
        borderStyle: 'solid',
      }}
    >
      {` +${photos.length - currentThumbnailCount}`}
    </div>
  );
  const selectedThumbnailStyle = {
    ...styles.thumbnail,
    ...styles.selectedThumbnailBorder,
    borderStyle: 'solid',
  };

  return (
    <VerticalLayout style={styles.containerStyle}>
      {!!title ? <span style={titleStyle}>{title}</span> : null}
      {getMainPhotoComponent()}
      <HorizontalLayout style={{ ...styles.thumbnailsContainer, flexWrap: 'wrap' }}>
        {allowEdit ? openUploaderButton : null}
        {photos.slice(0, currentThumbnailCount).map((photo, i) => {
          const thumbnailUrl = photo.thumbnailUrls?.small;
          const isCurrentlySelected = photoIndex === i;
          const style = isCurrentlySelected ? selectedThumbnailStyle : styles.thumbnail;
          return (
            <VerticalLayout style={thumbnailContainerStyle} key={`${thumbnailUrl}-${i}`}>
              <img
                key={thumbnailUrl}
                src={thumbnailUrl}
                alt=" "
                height={thumbnailHeight}
                width={thumbnailWidth}
                onClick={() => {
                  setPhotoIndex(i);
                }}
                style={style}
                data-testid={`thumbnail-${i}`}
              />
            </VerticalLayout>
          );
        })}
        {photos.length > currentThumbnailCount ? extraImgCount : null}
      </HorizontalLayout>
      <DropzoneDialog
        filesLimit={10}
        maxFileSize={10000000} // 10 MB
        open={showUploader}
        onSave={(files: any[]) => {
          onUpload(files, title.toLowerCase());
          setShowUploader(false);
        }}
        showAlerts={['error']}
        onClose={() => setShowUploader(false)}
        acceptedFiles={['image/*']}
      />
    </VerticalLayout>
  );
};

export default InlinePhotoViewer;

const styles = {
  containerStyle: {
    display: 'flex',
    height: '100%',
    width: '100%',
  },
  fullImageContainer: {
    display: 'flex',
    height: '50vh',
    width: '24vw',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F2F2F2',
    borderRadius: 10,
    marginTop: '1vh',
  },
  fullImage: {
    height: '100%',
    width: '100%',
    borderRadius: 10,
  },
  leftRightButton: {
    backgroundColor: 'white',
    borderRadius: '50%',
    height: 30,
    width: 30,
  },
  thumbnailsContainer: {
    display: 'flex',
    marginTop: '2vh',
  },
  thumbnail: {
    borderRadius: 10,
    marginRight: '0.5vw',
    cursor: 'pointer',
  },
  selectedThumbnailBorder: {
    borderWidth: 2,
    borderColor: '#1D75CF',
  },
  extraImgCount: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#526475',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    cursor: 'pointer',
  },
  addImage: {
    borderRadius: 10,
    marginRight: '0.5vw',
    cursor: 'pointer',
    borderWidth: 1,
    borderColor: '#526475',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
  },
};
